import { fromJS } from "immutable";
import { getNewProject } from "../containers/shorts/shorts-helper";
import { getWorkspaceItemSource } from "./URLHelper";
import { getAiReframeStaticX } from "./reframeHelper";

const loadVideoAtTime = (src, timeInSeconds, timeout = 8000) => new Promise((resolve, reject) => {
  const video = document.createElement("video");
  video.crossOrigin = "anonymous";
  video.muted = true;
  video.playsInline = true;
  video.preload = "auto";

  let settled = false;
  let timer = null;

  const finish = (fn, value) => {
    if (settled) return;
    settled = true;
    clearTimeout(timer);
    fn(value);
  };

  timer = setTimeout(() => {
    finish(reject, new Error("Video load timeout"));
  }, timeout);


  const seekAndResolve = () => {
    const onSeeked = () => {
      video.removeEventListener("seeked", onSeeked);
      finish(resolve, video);
    };
    video.addEventListener("seeked", onSeeked, { once: true });
    if (!Number.isFinite(timeInSeconds) || timeInSeconds < 0) {
      video.currentTime = 0;
    } else {
      video.currentTime = timeInSeconds;
    }
  };

  video.addEventListener("loadeddata", seekAndResolve, { once: true });
  video.addEventListener("error", () => finish(reject, new Error("Video load failed")), { once: true });
  video.src = src;
  video.load();
});

const getThumbDimensions = (projectWidth, projectHeight, maxRes = 352) => {
  const aspectRatio = projectWidth / projectHeight;
  let thumbWidth;
  let thumbHeight;

  if (projectWidth > projectHeight) {
    thumbWidth = maxRes;
    thumbHeight = thumbWidth / aspectRatio;
  } else if (projectWidth < projectHeight) {
    thumbHeight = maxRes;
    thumbWidth = aspectRatio * thumbHeight;
  } else {
    thumbWidth = maxRes;
    thumbHeight = thumbWidth;
  }

  return { thumbWidth, thumbHeight };
};

const buildLocalReframeData = (reframeDataJS, assetId, shortId) => {
  if (!reframeDataJS || !assetId) return null;
  const local = { ...reframeDataJS };
  if (shortId) {
    const compositeKey = `${assetId}_${shortId}`;
    if (local[compositeKey]) {
      local[assetId] = local[compositeKey];
    }
  }
  return local;
};

const getVisibleSceneItems = (workspaceItems, playhead) => (
  Object.values(workspaceItems)
    .filter((item) => (
      item.type === "UPVIDEO"
      && item.sceneData
      && item.originalId
      && playhead >= item.enterStart
      && playhead <= item.exitEnd
    ))
    .sort((a, b) => (a.track || 0) - (b.track || 0))
);

const drawSceneItem = ({
  ctx,
  video,
  item,
  playhead,
  scale,
  reframeData,
  appReframeData,
  projectWidth,
}) => {
  const containerX = item.x * scale;
  const containerY = item.y * scale;
  const containerW = item.width * scale;
  const containerH = item.height * scale;

  if (containerW <= 0 || containerH <= 0) return;

  ctx.save();
  ctx.globalAlpha = Number.isFinite(item.opacity) ? item.opacity : 1;
  ctx.beginPath();
  ctx.rect(containerX, containerY, containerW, containerH);
  ctx.clip();

  const { original = {} } = item;
  if (original) {
    const panX = getAiReframeStaticX(
      fromJS(item),
      playhead,
      reframeData,
      appReframeData,
      1,
      projectWidth
    ) || 0;

    const videoX = containerX + (original.x || 0) * containerW + panX * scale;
    const videoY = containerY + (original.y || 0) * containerH;
    const videoW = (original.width || 1) * containerW;
    const videoH = (original.height || 1) * containerH;

    ctx.drawImage(video, videoX, videoY, videoW, videoH);
  } else {
    ctx.drawImage(video, containerX, containerY, containerW, containerH);
  }

  ctx.restore();
};

/**
 * Canvas-composite a reframed short thumbnail (video layout, no subtitles).
 * Avoids html2canvas which cannot capture <video> elements.
 */
export const captureShortReframeThumbnail = async ({
  projectDetails,
  shortData,
  appReframeData,
  playheadOffset = 5,
  maxRes = 352,
}) => {
  const projectJS = projectDetails?.toJS ? projectDetails.toJS() : projectDetails;
  const shortJS = shortData?.toJS ? shortData.toJS() : shortData;
  const reframeDataJS = appReframeData?.toJS ? appReframeData.toJS() : appReframeData;

  if (!projectJS || !shortJS) return null;

  const clippedProject = getNewProject(projectJS, shortJS, reframeDataJS);
  const { width: projectWidth, height: projectHeight, workspaceItems } = clippedProject;

  const baseItem = Object.values(workspaceItems).find(
    (item) => item.type === "UPVIDEO" && !item.sceneId
  );
  if (!baseItem) return null;

  const sceneItems = Object.values(workspaceItems).filter(
    (item) => item.type === "UPVIDEO" && item.sceneData && item.originalId
  );
  if (sceneItems.length === 0) return null;

  const { src } = getWorkspaceItemSource({ item: baseItem });
  if (!src) return null;

  const clipDuration = clippedProject.duration || 0;
  const relativePlayhead = Math.min(
    Math.max(0, playheadOffset),
    Math.max(0, clipDuration - 0.05)
  );

  const referenceItem = sceneItems[0];
  const videoTime = (referenceItem.videoStart || baseItem.videoStart || 0)
    + (relativePlayhead - (referenceItem.enterStart || 0));

  const video = await loadVideoAtTime(src, videoTime);
  const { thumbWidth, thumbHeight } = getThumbDimensions(projectWidth, projectHeight, maxRes);
  const scale = thumbWidth / projectWidth;

  const canvas = document.createElement("canvas");
  canvas.width = thumbWidth;
  canvas.height = thumbHeight;
  const ctx = canvas.getContext("2d");
  if (!ctx) return null;

  ctx.fillStyle = clippedProject.bgColor || "#000000";
  ctx.fillRect(0, 0, thumbWidth, thumbHeight);

  const localReframeData = buildLocalReframeData(
    reframeDataJS,
    baseItem.assetId,
    shortJS.id
  );

  const visibleAtPlayhead = getVisibleSceneItems(workspaceItems, relativePlayhead);
  visibleAtPlayhead.forEach((item) => {
    drawSceneItem({
      ctx,
      video,
      item,
      playhead: relativePlayhead,
      scale,
      reframeData: localReframeData,
      appReframeData,
      projectWidth,
    });
  });

  return new Promise((resolve) => {
    canvas.toBlob((blob) => resolve(blob), "image/png");
  });
};

export default captureShortReframeThumbnail;
